// src/components/sideNavBar/sideNavBar.js
import { useNavigate, useLocation } from "react-router-dom";
import React, { useState, useEffect, useCallback } from "react";
import axios from "../../services/axiosConfig";
import { getAllowedNavItems } from "../../utils/navItems";
import { Menu, Dropdown } from "antd";
import { FaPhotoVideo } from "react-icons/fa";
import { getAuthToken } from "../../auth/auth";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import AppBadge from "../badge/badge";
import "@fontsource/oswald";
import "./sideNavBar.css";

function SideNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const allowedItems = getAllowedNavItems();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetches total unread count from member-updates API for the Member Management sidebar badge
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const res = await axios.get(API_ENDPOINTS.MEMBER_UPDATES, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.unreadCount || 0);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    const onRead = () => fetchUnreadCount();
    window.addEventListener("notificationRead", onRead);
    return () => window.removeEventListener("notificationRead", onRead);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const onFocus = () => fetchUnreadCount();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const id = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(id);
  }, [fetchUnreadCount]);

  // Get the current active key based on pathname
  const getActiveKey = () => {
    const pathname = location.pathname;
    const match = allowedItems.find((item) => item.path === pathname);
    if (match) return match.key;
    const lastSegment = pathname.split("/").pop();
    return allowedItems.find((item) => item.key === lastSegment)?.key || null;
  };

  const memberManagementLabel =
    unreadCount > 0 ? (
      <span className="sidebar-badge-row">
        <span>Member Management</span>
        <AppBadge
          className="sidebar-badge"
          title={`${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`}
          count={unreadCount}
        />
      </span>
    ) : (
      "Member Management"
    );

  const antdMenuItems = [];
  let mediaDropdownInserted = false;

  for (const item of allowedItems) {
    if (item.parentGroup === "media") {
      if (mediaDropdownInserted) continue;

      const mediaChildren = allowedItems.filter((i) => i.parentGroup === "media");

      antdMenuItems.push({
        key: "media",
        icon: <FaPhotoVideo />,
        label: (
          <Dropdown
            menu={{
              items: mediaChildren.map((child) => ({
                key: child.key,
                label: child.label,
                onClick: () => navigate(child.path)
              }))
            }}
            trigger={["hover"]}
            placement="right"
            overlayClassName="sidebar-dropdown"
            getPopupContainer={() => document.body}
          >
            <span style={{ width: "100%", display: "block" }}>Media & Content &nbsp; &gt;</span>
          </Dropdown>
        )
      });

      mediaDropdownInserted = true;
    } else {
      const label = item.key === "memberManagement" ? memberManagementLabel : item.label;
      antdMenuItems.push({
        key: item.key,
        icon: item.icon,
        label,
        onClick: () => navigate(item.path)
      });
    }
  }

  return (
    <div className="sidebar">
      <div className="sidebar-logo">
        <span>OurChurchLink&trade;</span>
      </div>
      <nav className="sidebar-nav">
        <Menu theme="dark" mode="inline" selectedKeys={[getActiveKey()]} items={antdMenuItems} className="sidebar-menu" />
      </nav>
    </div>
  );
}

export default SideNavBar;
